import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { CountryRegionData } from 'react-country-region-selector';

import ItemViewImages from './itemViewImages.js';
import PriceFormatter from '../components/priceFormatter';
import ItemViewMarketplaces from './itemViewMarketplaces.js';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import utils from '../utils/utils';
import { deleteItem } from './itemActions';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PREFERRED_PRICE_DISPLAY from '../components/constants/preferredPriceDisplay';

const ItemViewContent = ({ item, marketplace, currentUser }) => {
  const itemDescription = {
    __html: item.description,
  };

  const itemIngredients = {
    __html: item.ingredients,
  };

  const price = item.priceFields.price ? (
    <PriceFormatter price={item.priceFields.price} currency={item.priceFields.currency} />
  ) : (
    item.priceFields.priceText
  );

  const location = getItemLocation(item);

  const adminPanel =
    currentUser && (item.user.id === currentUser.id || utils.userIsAdmin(currentUser)) ? (
      <div className="col-12">
        <ul className="list-unstyled list-inline m-b-6">
          <li className="list-inline-item m-r-3">
            <Link className="link" to={`/marketplaceAdmin/${marketplace.id}/item/edit/${item.id}`}>
              <FontAwesomeIcon icon={faWrench} /> <FormattedMessage id={'item.view.admin.links.edit'} />
            </Link>
          </li>
        </ul>
      </div>
    ) : (
      ''
    );

  const showUnitPrice =
    item.priceFields.unitPrice && item.priceFields.preferredPriceDisplay === PREFERRED_PRICE_DISPLAY.unitPrice;

  const { formatMessage } = useIntl();

  return (
    <div className="row item-view">
      <Helmet>
        <title>{utils.getItemName(item, formatMessage)}</title>
        <meta name="description" content={item.shortDescription} />
      </Helmet>

      {adminPanel}

      <div className="col-12 col-xl-8">
        <ItemViewImages item={item} />
      </div>
      <div className="col-12 col-xl-4">
        <div className="item-view__details">
          <h2 className="item-view__details-title">{utils.getItemName(item, formatMessage)}</h2>
          <div>
            <small className="text-primary text-uppercase text-semi-bold">
              <FormattedMessage id={'item.view.right.price.label'} />
            </small>
          </div>
          <p className="item-view__details-price m-b-0">
            <span className="text-large text-semi-bold">
              {showUnitPrice ? (
                <>
                  <PriceFormatter price={item.priceFields.price} currency={item.priceFields.currency} /> /{' '}
                  {item.priceFields.standardUnitQuantity} kg
                </>
              ) : (
                <PriceFormatter price={item.priceFields.price} currency={item.priceFields.currency} />
              )}
            </span>
          </p>

          <hr className="item-view__details-separator" />

          <div
            className="item-view__details-description text-rich-text m-b-3"
            dangerouslySetInnerHTML={itemDescription}
          />

          {item.ingredients ? (
            <div className="item-view__details-ingredients text-rich-text p-a-3 m-b-6">
              <div>
                <small>
                  <strong>
                    <FormattedMessage id={'item.view.right.ingredients.label'} />:
                  </strong>
                </small>
              </div>
              <div dangerouslySetInnerHTML={itemIngredients} />
            </div>
          ) : (
            ''
          )}

          <hr className="item-view__details-separator" />

          <ItemViewMarketplaces item={item} marketplace={marketplace} />
        </div>
      </div>
    </div>
  );
};

function getItemLocation(item) {
  if (!item.address || !item.address.country) {
    return '';
  }

  const street = item.address.street ? item.address.street + ', ' : '';
  const city = item.address.city ? item.address.city + ', ' : '';
  const postCode = item.address.postCode ? item.address.postCode + ', ' : '';
  const country = CountryRegionData.find((country) => country[1] === item.address.country);

  return country ? (
    <div>
      <small>
        <i className="fa fa-map-marker" /> {street}
        {city}
        {postCode}
        {country[0]}
      </small>
    </div>
  ) : (
    ''
  );
}

ItemViewContent.propTypes = {
  item: PropTypes.object.isRequired,
  marketplace: PropTypes.object,
};

ItemViewContent.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {
  deleteItem,
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemViewContent);
